/**
 * API client.
 *
 * The access token lives in memory only: a token in localStorage is readable by
 * any script that gets onto the page. The refresh token is an HttpOnly cookie
 * the browser sends to /api/v1/auth/refresh and nothing else, so a reload
 * restores the session without ever exposing a long-lived credential to JS.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error?.code ?? "unknown_error";
    this.details = body?.error?.details ?? {};
  }

  /** Field-level messages from a 422, keyed by form field path. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details.fields;
    return typeof fields === "object" && fields !== null
      ? (fields as Record<string, string>)
      : {};
  }
}

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAuthenticationLost(handler: () => void): void {
  onAuthLost = handler;
}

const BASE = "/api/v1";

/**
 * Single-flight refresh.
 *
 * Refresh tokens rotate, and presenting one that has already been rotated is
 * treated as theft: the whole token family is revoked. Two refreshes racing
 * would therefore sign the user out, so every caller shares one attempt.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access_token?: string };
      if (!data.access_token) return false;
      accessToken = data.access_token;
      return true;
    } catch {
      return false;
    } finally {
      // Held briefly after settling so a caller that starts moments later
      // joins this attempt instead of replaying the rotated token.
      setTimeout(() => {
        refreshInFlight = null;
      }, 250);
    }
  })();
  return refreshInFlight;
}

/** Values that can appear in a query string. */
export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the refresh-and-retry dance. Used by the auth endpoints themselves. */
  skipAuthRetry?: boolean;
  query?: QueryParams;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { body, query, skipAuthRetry: _skip, headers, ...rest } = options;
  const isFormData = body instanceof FormData;

  const finalHeaders = new Headers(headers);
  finalHeaders.set("X-Requested-With", "fetch");
  if (accessToken) finalHeaders.set("Authorization", `Bearer ${accessToken}`);
  if (body !== undefined && !isFormData) finalHeaders.set("Content-Type", "application/json");

  return fetch(buildUrl(path, query), {
    ...rest,
    headers: finalHeaders,
    credentials: "include",
    body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && !options.skipAuthRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send(path, options);
    } else {
      accessToken = null;
      onAuthLost?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiErrorBody | null, response.statusText);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
